<template>
  <div>
    <div class="flex items-center justify-between px-5 h-8">
      <div class="flex items-center space-x-2">
        <IconSparkles class="w-6 h-6 stroke-1" />
        <span class="font-semibold">Ask DocuSeal AI</span>
      </div>
      <div
        v-if="(!isTyping && assistantMessage.length > 0) || messages.length > 2"
        class="space-x-2.5"
      >
        <div
          class="tooltip tooltip-bottom before:z-50"
          :data-tip="copyTip"
          @click="[copyToClipboard(assistantMessage[assistantMessage.length - 1]?.content), copyTip = 'Copied']"
          @mouseenter="copyTip = 'Copy'"
        >
          <IconCopy
            class="w-5 h-5 text-base-content/80 cursor-pointer hover:text-base-content transform active:scale-75 transition-transform"
          />
        </div>
        <div
          class="tooltip tooltip-bottom before:z-50"
          :data-tip="shareTip"
          @mouseenter="shareTip = 'Share'"
          @click="[copyToClipboard(`${websiteUrl}/chat#${chatSessionUuid}`), shareTip = 'Copied']"
        >
          <IconShare
            class="w-5 h-5 text-base-content/80 cursor-pointer hover:text-base-content transform active:scale-75 transition-transform"
          />
        </div>
        <div
          class="tooltip tooltip-bottom before:z-50"
          data-tip="Clear"
          @click="reset"
        >
          <IconRefresh
            class="w-5 h-5 text-base-content/80 cursor-pointer hover:text-base-content transform active:scale-75 transition-transform"
          />
        </div>
      </div>
    </div>
    <div
      v-if="showIntro"
      class="px-5 py-4 text-sm text-center border-b leading-relaxed border-base-300 text-base-500"
    >
      <div class="max-w-lg mx-auto">
        DocuSeal AI can help you learn about product features and pricing, guide you through different workflows, and even generate integration code.
      </div>
    </div>
    <div
      v-if="messages.length"
      class="relative"
    >
      <div
        v-if="!sharedChat"
        class="pointer-events-none absolute top-0 left-0 w-full h-4 bg-gradient-to-b from-base-100 to-transparent z-10"
      />
      <div
        ref="chatContainer"
        class="px-5 overflow-y-auto hover:overflow-y-scroll divide-y divide-base-300"
        :style="sharedChat ? {} : messagesContainerStyle"
      >
        <div
          v-for="(msg, index) in messages"
          :key="index"
          class="flex py-4 space-x-2 md:space-x-4"
        >
          <component
            :is="msg.role === 'user' ? 'IconUserCircle' : 'IconSparkles'"
            class="w-6 h-6 md:w-8 md:h-8 stroke-1 shrink-0"
          />
          <div
            v-if="msg.role === 'user'"
            class="px-1 prose prose-tight w-full"
          >
            <div
              v-for="(line, i) in msg.content.split('\n')"
              :key="i"
            >
              {{ line }}
            </div>
          </div>
          <div v-else>
            <div
              class="px-1 prose prose-tight w-full"
              v-html="renderMarkdown(msg.content)"
            />
            <div
              v-if="isSearching && index === messages.length - 1"
              class="ml-1 loading loading-dots"
            />
          </div>
        </div>
        <div
          v-if="isThinking"
          class="flex py-4 space-x-5"
        >
          <IconSparkles class="w-8 h-8 stroke-1 shrink-0" />
          <span class="loading loading-dots" />
        </div>
      </div>
      <div class="pointer-events-none absolute bottom-0 left-0 w-full h-4 bg-gradient-to-t from-base-100 to-transparent z-10" />
    </div>
    <div
      v-if="sharedChat && !isLoading"
      class="px-5 mt-4"
    >
      <button
        class="btn btn-neutral normal-case text-white text-base w-full !animate-none"
        @click="[reset(), focusInput()]"
      >
        Ask a Question
      </button>
    </div>
    <div
      v-else-if="!isLoading"
      class="relative px-5 pt-3"
      :class="{ 'border-t border-base-300': messages.length }"
    >
      <form
        class="flex items-end space-x-2"
        @submit.prevent="sendMessage"
      >
        <textarea
          ref="questionInput"
          v-model="userMessage"
          type="text"
          rows="1"
          placeholder="Ask anything..."
          class="input w-full h-min p-0 border-none rounded-none outline-none focus:outline-none text-lg resize-none placeholder:text-base-400/60"
          @keydown.enter.exact.prevent="sendMessage"
        />
        <button
          class="btn btn-sm btn-neutral btn-circle"
          :class="{ 'text-base-100': !!userMessage, '!text-base-400': !userMessage }"
          :disabled="!userMessage"
        >
          <IconArrowUp class="w-6 h-6" />
        </button>
      </form>
    </div>
    <div
      v-else-if="isLoading"
      class="mt-2 flex items-center justify-center gap-2 text-sm"
    >
      <span class="loading loading-spinner loading-sm" />
    </div>
  </div>
</template>

<script>
import { v4 } from 'uuid'
import { IconSearch, IconSparkles, IconArrowUp, IconUserCircle, IconCopy, IconShare, IconRefresh } from '@tabler/icons-vue'

export default {
  name: 'ChatTab',
  components: {
    IconSearch,
    IconUserCircle,
    IconSparkles,
    IconArrowUp,
    IconCopy,
    IconShare,
    IconRefresh
  },
  inject: ['baseUrl', 'websiteUrl'],
  props: {
    withSearchButton: {
      type: Boolean,
      required: false,
      default: false
    },
    withSharedChat: {
      type: Boolean,
      required: false,
      default: false
    },
    messagesContainerStyle: {
      type: Object,
      required: false,
      default: () => ({ maxHeight: 'calc(100vh - 15rem)' })
    }
  },
  emits: ['use-search'],
  data () {
    return {
      copyTip: 'Copy',
      shareTip: 'Share',
      chatSessionUuid: null,
      userMessage: '',
      messages: [],
      renderMarkdownFunction: null,
      isTyping: false,
      isLoading: false,
      isThinking: false,
      isSearching: false,
      sharedChat: false,
      inToolCall: false
    }
  },
  computed: {
    assistantMessage () {
      return this.messages.filter(msg => msg.role === 'assistant')
    },
    showIntro () {
      return !this.sharedChat && !this.isLoading && !this.isThinking && !this.messages.length
    }
  },
  watch: {
    userMessage () {
      this.$nextTick(() => {
        const el = this.$refs.questionInput
        el.style.height = 'auto'
        el.style.height = el.scrollHeight + 'px'
      })
    }
  },
  mounted () {
    const sharedSessionUuid = this.withSharedChat ? window.location.hash.substring(1) : ''

    if (
      sharedSessionUuid?.length === 36 &&
      sharedSessionUuid[8] === '-' &&
      sharedSessionUuid[13] === '-' &&
      sharedSessionUuid[18] === '-' &&
      sharedSessionUuid[23] === '-'
    ) {
      this.isLoading = true

      Promise.all([
        this.loadMarkdownRenderer(),
        fetch(`${this.baseUrl}/chat/${sharedSessionUuid}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        }).then(response => {
          if (response.ok) {
            return response.json()
          } else {
            this.focusInput()
          }
        }).catch(error => {
          console.error('Error fetching chat messages:', error)
        })]).then(([, data]) => {
        if (data) {
          this.messages = data.messages
          this.chatSessionUuid = sharedSessionUuid
          this.sharedChat = true
        }
      }).finally(() => {
        this.isLoading = false
      })
    } else {
      this.focusInput()
    }
  },
  methods: {
    async loadMarkdownRenderer () {
      if (this.renderMarkdownFunction) return this.renderMarkdownFunction

      const [
        { Marked },
        { default: DOMPurify },
        { markedHighlight },
        { default: hljs },
        { default: plaintext },
        { default: python },
        { default: ruby },
        { default: php },
        { default: bash },
        { default: xml },
        { default: javascript },
        { default: csharp },
        { default: json },
        { default: go },
        { default: css },
        { default: codeStyle }
      ] = await Promise.all([
        import(/* webpackChunkName: "markdown" */ 'marked'),
        import(/* webpackChunkName: "markdown" */ 'dompurify'),
        import(/* webpackChunkName: "markdown" */ 'marked-highlight'),
        import(/* webpackChunkName: "markdown" */ 'highlight.js/lib/core'),
        import(/* webpackChunkName: "markdown" */ 'highlight.js/lib/languages/plaintext'),
        import(/* webpackChunkName: "markdown" */ 'highlight.js/lib/languages/python'),
        import(/* webpackChunkName: "markdown" */ 'highlight.js/lib/languages/ruby'),
        import(/* webpackChunkName: "markdown" */ 'highlight.js/lib/languages/php'),
        import(/* webpackChunkName: "markdown" */ 'highlight.js/lib/languages/bash'),
        import(/* webpackChunkName: "markdown" */ 'highlight.js/lib/languages/xml'),
        import(/* webpackChunkName: "markdown" */ 'highlight.js/lib/languages/javascript'),
        import(/* webpackChunkName: "markdown" */ 'highlight.js/lib/languages/csharp'),
        import(/* webpackChunkName: "markdown" */ 'highlight.js/lib/languages/json'),
        import(/* webpackChunkName: "markdown" */ 'highlight.js/lib/languages/go'),
        import(/* webpackChunkName: "markdown" */ 'highlight.js/lib/languages/css'),
        import(/* webpackChunkName: "markdown" */ 'highlight.js/styles/github-dark.css')
      ])

      const style = document.createElement('style')

      style.textContent = codeStyle[0][1].replaceAll('pre code.hljs', 'hljs-removed').replaceAll('.hljs{', '.hljs-removed{').replaceAll('.hljs {', '.hljs-removed{')

      this.$el.getRootNode().appendChild(style)

      hljs.registerLanguage('csharp', csharp)
      hljs.registerLanguage('go', go)
      hljs.registerLanguage('css', css)
      hljs.registerLanguage('json', json)
      hljs.registerLanguage('javascript', javascript)
      hljs.registerLanguage('typescript', javascript)
      hljs.registerLanguage('python', python)
      hljs.registerLanguage('ruby', ruby)
      hljs.registerLanguage('php', php)
      hljs.registerLanguage('bash', bash)
      hljs.registerLanguage('xml', xml)
      hljs.registerLanguage('plaintext', plaintext)

      const markedInstance = new Marked(
        markedHighlight({
          emptyLangClass: 'hljs',
          langPrefix: 'hljs language-',
          highlight (code, lang) {
            const language = lang === 'nodejs' ? 'javascript' : (hljs.getLanguage(lang) ? lang : 'plaintext')
            return hljs.highlight(code, { language }).value
          }
        }),
        {
          renderer: {
            html: ({ text }) => this.escapeHtml(text)
          }
        }
      )

      this.renderMarkdownFunction = (text) => {
        const body = DOMPurify.sanitize(markedInstance.parse(text), {
          ALLOWED_TAGS: ['a', 'blockquote', 'br', 'code', 'del', 'em', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'li', 'ol', 'p', 'pre', 'span', 'strong', 'table', 'tbody', 'td', 'th', 'thead', 'tr', 'ul'],
          ALLOWED_ATTR: ['align', 'class', 'href', 'start', 'title'],
          ALLOW_DATA_ATTR: false,
          ALLOW_ARIA_ATTR: false,
          FORCE_BODY: true,
          RETURN_DOM: true
        })

        body.querySelectorAll('[class]').forEach((el) => {
          el.className = [...el.classList].filter((name) => /^(hljs|language-)|_$/.test(name)).join(' ')
        })

        body.querySelectorAll('a').forEach((link) => link.setAttribute('target', '_blank'))

        return body.innerHTML
      }

      return this.renderMarkdownFunction
    },
    sendMessage () {
      if (this.isThinking || this.isTyping || this.userMessage.trim() === '') return

      this.loadMarkdownRenderer()
      this.chatSessionUuid ||= v4()
      this.isTyping = true
      this.isThinking = true

      this.messages.push({ role: 'user', content: this.userMessage })

      this.scrollDownMessages()

      fetch(this.baseUrl + '/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          session_uuid: this.chatSessionUuid,
          message: this.userMessage,
          page_url: window.location.href,
          tz: Intl.DateTimeFormat().resolvedOptions().timeZone
        })
      }).then(response => {
        const reader = response.body.getReader()
        const decoder = new TextDecoder('utf-8')
        let done = false
        let buffer = ''

        const dispatchEvent = (eventType, data) => {
          if (eventType === 'searching') {
            this.isSearching = true

            if (!this.inToolCall) {
              const text = JSON.parse(data)
              const lastIdx = this.messages.length - 1

              if (this.messages[lastIdx]?.role === 'assistant') {
                this.messages[lastIdx].content += text
              } else {
                this.messages.push({ role: 'assistant', content: text })
                this.isThinking = false
              }

              this.inToolCall = true
            }
          } else if (eventType === 'text') {
            this.isSearching = false

            const text = JSON.parse(data)

            if (!text) return

            const lastIdx = this.messages.length - 1

            if (this.inToolCall) {
              this.messages[lastIdx].content = text
              this.inToolCall = false
            } else if (this.messages[lastIdx]?.role === 'assistant') {
              this.messages[lastIdx].content += text
            } else {
              this.messages.push({ role: 'assistant', content: text })
              this.isThinking = false
            }
          }
        }

        const processStream = () => {
          return reader.read().then(({ value, done: readerDone }) => {
            done = readerDone
            buffer += decoder.decode(value || new Uint8Array(), { stream: !done })

            const blocks = buffer.split('\n\n')
            buffer = blocks.pop()

            blocks.forEach((block) => {
              if (!block.trim()) return

              let eventType = 'text'
              let data = ''

              block.split('\n').forEach((line) => {
                if (line.startsWith('event: ')) {
                  eventType = line.slice(7)
                } else if (line.startsWith('data: ')) {
                  data = line.slice(6)
                }
              })

              dispatchEvent(eventType, data)
            })

            this.isTyping = !done

            if (done) {
              this.isSearching = false
              this.inToolCall = false
            }

            this.scrollDownMessages()

            if (!done) return processStream()
          })
        }

        processStream()
      }).catch(error => {
        console.error('Error in streaming message: ', error)
      }).finally(() => {
        this.isThinking = false
      })

      this.userMessage = ''
    },
    reset () {
      this.messages = []
      this.chatSessionUuid = null
      this.isSearching = false
      this.inToolCall = false

      if (this.sharedChat) {
        this.sharedChat = false
        window.history.pushState(null, null, window.location.pathname + window.location.search)
      }
    },
    copyToClipboard (text) {
      if (!text) return

      if (navigator.clipboard) {
        navigator.clipboard.writeText(text)
      } else {
        alert(`Clipboard not available. Make sure you're using https://\nCopy text: ${text}`)
      }
    },
    focusInput () {
      this.$nextTick(() => {
        this.$refs.questionInput?.focus()
      })
    },
    escapeHtml (text) {
      return text.replace(/[&<>"']/g, (char) => `&#${char.charCodeAt(0)};`)
    },
    renderMarkdown (text) {
      if (this.renderMarkdownFunction) {
        return this.renderMarkdownFunction(text)
      } else {
        return this.escapeHtml(text)
      }
    },
    scrollDownMessages () {
      this.$nextTick(() => {
        const chatContainer = this.$refs.chatContainer

        if (chatContainer) {
          chatContainer.scrollTo({
            top: chatContainer.scrollHeight,
            behavior: 'smooth'
          })
        }
      })
    }
  }
}
</script>
